// TASKS surface — the board (N-BOARD-1), recreated 1:1 from the prototype (brand/ritona-app-handoff
// screens.js tasks): the sticky toolbar carries the grouping filters, the open/blocked stamp + the open-work
// cap (AC-4, no hidden default), the collapse toggles for the terminal columns, and the info affordance; the
// body is the flat hairline board. The columns are EXACTLY the Task lifecycle state enum the projection
// carries (G-182): they derive from the durable lifecycle, never a UI-authored column. Block renders
// WHOLE-Task only (AC-1a, no lanes): the parked/blocked view surfaces reason + awaited party + next check at
// Task granularity.
//
// Thin over services: the surface renders the `tasks` board projection; it routes no write here (Task work
// is proposed/briefed via the agent/chat transport, not a board control — brief §2). State/owner/KPI are
// view-only arrangements of that one projection. Tokens only (G-181).

import { useEffect, useState } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { subscribeToTaskProjectionChanges } from './task-projection-events.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { TaskBoard } from './components/TaskBoard.tsx';
import {
  ScreenToolbar,
  ScreenBody,
  TbFilter,
  TbStamp,
  TbDivider,
  TbInfo,
} from '../app/screen-chrome.tsx';
import { Icon } from '../app/Icon.tsx';
import type { TaskBoardProjection, TaskBoardWire, TaskState } from './projections.ts';
import type { TaskBoardGrouping } from './task-board-groups.ts';

/** The terminal columns the prototype ships collapsed (screens.js BOARD collapsible: Done/Archived). */
const COLLAPSIBLE: readonly TaskState[] = ['done', 'dropped'];

/**
 * FLATTEN the server wire (`{ board, filtered, parked }`) into the shape the board/stat components consume —
 * the server nests the histogram + cap/openCount/headroom under `board` (composeTaskBoardView; BUILD-DEC-091).
 */
function flattenBoard(wire: TaskBoardWire): TaskBoardProjection {
  return {
    countsByState: wire.board.countsByState,
    openCount: wire.board.openCount,
    cap: wire.board.cap,
    headroom: wire.board.headroom,
    filtered: wire.filtered,
    parked: wire.parked,
  };
}

/** Three mutually exclusive, view-only Task-board arrangements. */
function GroupFilters({
  grouping,
  onSelect,
}: {
  readonly grouping: TaskBoardGrouping;
  readonly onSelect: (grouping: TaskBoardGrouping) => void;
}): React.JSX.Element {
  return (
    <>
      <TbFilter label="By state" on={grouping === 'state'} onClick={() => onSelect('state')} />
      <TbFilter label="By owner" on={grouping === 'owner'} onClick={() => onSelect('owner')} />
      <TbFilter label="By metric" on={grouping === 'metric'} onClick={() => onSelect('metric')} />
    </>
  );
}

/** The toolbar collapse toggle for one terminal column (prototype .col-toggle; pressed = expanded). */
function ColToggle({
  state,
  count,
  expanded,
  onToggle,
}: {
  readonly state: TaskState;
  readonly count: number;
  readonly expanded: boolean;
  readonly onToggle: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="col-toggle"
      aria-pressed={expanded}
      data-toggle-col={state}
      onClick={onToggle}
    >
      <Icon name="chevron" size={11} />
      <span>{state}</span>
      <span className="tct">{count}</span>
    </button>
  );
}

/** The open/blocked/cap stamp + the terminal-column collapse toggles (board-dependent toolbar controls). */
function BoardControls({
  board,
  grouping,
  expanded,
  onToggle,
}: {
  readonly board: TaskBoardProjection;
  readonly grouping: TaskBoardGrouping;
  readonly expanded: Readonly<Record<string, boolean>>;
  readonly onToggle: (column: TaskState) => void;
}): React.JSX.Element {
  const blocked = board.countsByState.blocked ?? 0;
  return (
    <>
      <TbStamp>
        {board.openCount} open · {blocked} blocked
        {board.cap !== null ? ` · cap ${String(board.cap)}` : ''}
      </TbStamp>
      {grouping === 'state' ? (
        <div className="col-toggles">
          {COLLAPSIBLE.map((column) => (
            <ColToggle
              key={column}
              state={column}
              count={board.countsByState[column] ?? 0}
              expanded={expanded[column] ?? false}
              onToggle={() => onToggle(column)}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

/** The Tasks board surface. */
export function TasksSurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const state = useProjection<TaskBoardWire>(api.getTaskBoard);
  useEffect(() => subscribeToTaskProjectionChanges(state.reload), [state.reload]);
  // The terminal columns start collapsed, exactly as the prototype ships them.
  const [expanded, setExpanded] = useState<Readonly<Record<string, boolean>>>({});
  const [grouping, setGrouping] = useState<TaskBoardGrouping>('state');
  const toggleColumn = (column: TaskState): void =>
    setExpanded((current) => ({ ...current, [column]: !(current[column] ?? false) }));

  const board = state.data !== null ? flattenBoard(state.data) : null;

  return (
    <>
      <ScreenToolbar title="Tasks">
        <GroupFilters grouping={grouping} onSelect={setGrouping} />
        <TbDivider />
        {board !== null ? (
          <BoardControls
            board={board}
            grouping={grouping}
            expanded={expanded}
            onToggle={toggleColumn}
          />
        ) : null}
        <TbInfo
          title="Tasks"
          tip="Columns are the task's own state — draft to done. Each card is a thread you can open, not a status to keep."
        />
      </ScreenToolbar>
      <ScreenBody>
        <SurfaceFrame state={state}>
          {(wire) => {
            const loaded = flattenBoard(wire);
            // Parked/blocked renders ON the card (reason + clock, exactly the prototype) — no separate
            // section below the board; the board IS the whole view.
            return (
              <section aria-label="Task board" className="group">
                <TaskBoard
                  board={loaded}
                  grouping={grouping}
                  expanded={expanded}
                  collapsible={COLLAPSIBLE}
                />
              </section>
            );
          }}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}
